import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Statistic, 
  Typography, 
  Input, 
  Select, 
  Button, 
  Space, 
  Tag, 
  List, 
  Avatar, 
  Progress, 
  Modal, 
  Descriptions, 
  Alert,
  Spin,
  message,
  Divider
} from 'antd';
import { 
  AuditOutlined, 
  PlayCircleOutlined, 
  CheckCircleOutlined, 
  ClockCircleOutlined, 
  ExclamationCircleOutlined,
  BookOutlined,
  BarChartOutlined,
  FileTextOutlined,
  BulbOutlined,
  SearchOutlined
} from '@ant-design/icons';
import { 
  getAvailableCourses, 
  getAuditDashboard, 
  startCourseAudit,
  getCourses
} from './api';

interface Course {
  id: number;
  title: string;
  description: string;
  lessons?: any[];
  _count?: {
    lessons: number;
  };
  lessonsCount?: number;
  lastAudit?: any;
  isAvailable?: boolean;
}

interface Audit {
  id: number;
  courseId: number;
  auditType: string;
  status: string;
  overallScore: number | null;
  aiReport: string | null;
  recommendations: string | null;
  createdAt: string;
  completedAt: string | null;
  course: {
    title: string;
  };
  auditResults: Array<{
    criterion: string;
    score: number;
    feedback: string;
    evidence: string | null;
    suggestions: string | null;
  }>;
}

const { Title, Text } = Typography;
const { Option } = Select;

const CourseAuditor: React.FC = () => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [audits, setAudits] = useState<Audit[]>([]);
  const [courseId, setCourseId] = useState<string>('');
  const [auditType, setAuditType] = useState<string>('comprehensive');
  const [aiAssistant, setAiAssistant] = useState<string>('gpt');
  const [loading, setLoading] = useState(false);
  const [selectedAudit, setSelectedAudit] = useState<Audit | null>(null);
  const [selectedCourseInfo, setSelectedCourseInfo] = useState<Course | null>(null);
  const [auditModalVisible, setAuditModalVisible] = useState(false);

  useEffect(() => {
    fetchCourses();
    fetchRecentAudits();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await getCourses();
      setCourses(response);
    } catch (error) {
      console.error('Ошибка при загрузке курсов:', error);
      message.error('Ошибка при загрузке курсов');
    }
  };

  const fetchRecentAudits = async () => {
    try {
      const response = await getAuditDashboard(20);
      setAudits(response.recentAudits);
    } catch (error) {
      console.error('Ошибка при загрузке аудитов:', error);
      message.error('Ошибка при загрузке аудитов');
    }
  };

  const findCourseById = (id: string) => {
    const course = courses.find(c => c.id.toString() === id);
    setSelectedCourseInfo(course || null);
    return course;
  };

  const startAudit = async () => {
    if (!courseId.trim()) {
      message.warning('Введите ID курса для аудита');
      return;
    }

    const course = findCourseById(courseId);
    if (!course) {
      message.error('Курс с таким ID не найден');
      return;
    }

    setLoading(true);
    try {
      await startCourseAudit({
        courseId: parseInt(courseId),
        auditType: auditType,
        aiAssistant: aiAssistant,
        depth: 3
      });
      
      message.success(`Аудит курса "${course.title}" запущен! Обновите страницу через несколько минут для просмотра результатов.`);
      fetchRecentAudits();
      setCourseId('');
      setSelectedCourseInfo(null);
    } catch (error) {
      console.error('Ошибка при запуске аудита:', error);
      message.error('Ошибка при запуске аудита');
    } finally {
      setLoading(false);
    }
  };

  const getStatusTag = (status: string) => {
    switch (status) {
      case 'completed': 
        return <Tag color="green" icon={<CheckCircleOutlined />}>Завершен</Tag>;
      case 'in_progress': 
        return <Tag color="blue" icon={<ClockCircleOutlined />}>В процессе</Tag>;
      case 'failed': 
        return <Tag color="red" icon={<ExclamationCircleOutlined />}>Ошибка</Tag>;
      default: 
        return <Tag>{status}</Tag>;
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'normal';
    return 'exception';
  };

  const handleAuditClick = (audit: Audit) => {
    setSelectedAudit(audit);
    setAuditModalVisible(true);
  };

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <Title level={2} style={{ color: "#1976d2", margin: 0 }}>
          <AuditOutlined style={{ marginRight: 12 }} />
          ИИ-Аудитор Курсов
        </Title>
        <Text type="secondary" style={{ fontSize: 16 }}>
          Автоматический анализ качества образовательного контента с помощью искусственного интеллекта
        </Text>
      </div>
      
      {/* Статистика */}
      <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Всего курсов"
              value={courses.length}
              prefix={<BookOutlined />}
              valueStyle={{ color: '#1976d2' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Доступно для аудита"
              value={courses.filter(c => (c.lessons?.length || c._count?.lessons || c.lessonsCount || 0) > 0).length}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Завершенных аудитов"
              value={audits.filter(a => a.status === 'completed').length}
              prefix={<BarChartOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="В процессе"
              value={audits.filter(a => a.status === 'in_progress').length}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[24, 24]}>
        {/* Запуск нового аудита */}
        <Col xs={24} lg={12}>
          <Card 
            title={
              <Space>
                <PlayCircleOutlined />
                Запустить новый аудит
              </Space>
            }
            style={{ height: '100%' }}
          >
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div>
                <Text strong>ID курса</Text>
                <Input
                  type="number"
                  value={courseId}
                  onChange={(e) => {
                    setCourseId(e.target.value);
                    if (e.target.value) {
                      findCourseById(e.target.value);
                    } else {
                      setSelectedCourseInfo(null);
                    }
                  }}
                  placeholder="Введите ID курса (например: 1)"
                  prefix={<SearchOutlined />}
                  size="large"
                />
                
                {selectedCourseInfo && (
                  <Alert
                    message={selectedCourseInfo.title}
                    description={`${selectedCourseInfo.lessons?.length || selectedCourseInfo._count?.lessons || selectedCourseInfo.lessonsCount || 0} уроков`}
                    type="success"
                    showIcon
                    style={{ marginTop: 8 }}
                  />
                )}
              </div>

              <div>
                <Text strong>Тип аудита</Text>
                <Select
                  value={auditType}
                  onChange={setAuditType}
                  size="large"
                  style={{ width: '100%' }}
                >
                  <Option value="comprehensive">🔍 Комплексный аудит</Option>
                  <Option value="quality">⭐ Оценка качества</Option>
                  <Option value="completeness">📚 Полнота курса</Option>
                  <Option value="difficulty">⚖️ Соответствие сложности</Option>
                </Select>
              </div>

              <div>
                <Text strong>ИИ для проведения аудита</Text>
                <Select
                  value={aiAssistant}
                  onChange={setAiAssistant}
                  size="large"
                  style={{ width: '100%' }}
                >
                  <Option value="gpt">
                    <Space>
                      <span>🤖</span>
                      <span>GPT-4 (OpenAI)</span>
                    </Space>
                  </Option>
                  <Option value="claude">
                    <Space>
                      <span>🧠</span>
                      <span>Claude Sonnet (Anthropic)</span>
                    </Space>
                  </Option>
                </Select>
                <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
                  {aiAssistant === 'gpt' 
                    ? 'Быстрый и эффективный анализ с фокусом на структуру' 
                    : 'Глубокий анализ с акцентом на качество контента и понимание'
                  }
                </Text>
              </div>

              <Button
                type="primary"
                size="large"
                icon={<PlayCircleOutlined />}
                onClick={startAudit}
                loading={loading}
                disabled={!courseId.trim()}
                block
              >
                Запустить аудит
              </Button>
            </Space>
          </Card>
        </Col>

        {/* Последние аудиты */}
        <Col xs={24} lg={12}>
          <Card 
            title={
              <Space>
                <FileTextOutlined />
                Последние аудиты
              </Space>
            }
            style={{ height: '100%' }}
          >
            {audits.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <FileTextOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
                <Text type="secondary">Пока нет проведенных аудитов</Text>
              </div>
            ) : (
              <List
                dataSource={audits}
                renderItem={(audit) => (
                  <List.Item
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleAuditClick(audit)}
                    actions={[
                      audit.overallScore && (
                        <Progress
                          type="circle"
                          size={40}
                          percent={audit.overallScore}
                          status={getScoreColor(audit.overallScore)}
                          format={() => `${audit.overallScore?.toFixed(0) || 0}`}
                        />
                      )
                    ]}
                  >
                    <List.Item.Meta
                      avatar={<Avatar icon={<AuditOutlined />} />}
                      title={audit.course.title}
                      description={
                        <Space>
                          <Text type="secondary" style={{ textTransform: 'capitalize' }}>
                            {audit.auditType}
                          </Text>
                          <Text type="secondary">•</Text>
                          <Text type="secondary">
                            {new Date(audit.createdAt).toLocaleDateString()}
                          </Text>
                        </Space>
                      }
                    />
                    <div>
                      {getStatusTag(audit.status)}
                    </div>
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>
      </Row>

      {/* Модальное окно с детальным просмотром аудита */}
      <Modal
        title={
          <Space>
            <AuditOutlined />
            Отчет по аудиту: {selectedAudit?.course.title}
          </Space>
        }
        open={auditModalVisible}
        onCancel={() => {
          setAuditModalVisible(false);
          setSelectedAudit(null);
        }}
        footer={null}
        width={1200}
        style={{ top: 20 }}
      >
        {selectedAudit && (
          <Row gutter={[24, 24]}>
            {/* Общая информация */}
            <Col xs={24} lg={8}>
              <Card title="Общая информация" size="small">
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="Тип аудита">
                    <Tag>{selectedAudit.auditType}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Статус">
                    {getStatusTag(selectedAudit.status)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Общая оценка">
                    {selectedAudit.overallScore ? (
                      <Progress
                        type="circle"
                        size={60}
                        percent={selectedAudit.overallScore}
                        status={getScoreColor(selectedAudit.overallScore)}
                        format={() => `${selectedAudit.overallScore?.toFixed(0) || 0}`}
                      />
                    ) : (
                      <Text type="secondary">N/A</Text>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Дата создания">
                    {new Date(selectedAudit.createdAt).toLocaleString()}
                  </Descriptions.Item>
                  {selectedAudit.completedAt && (
                    <Descriptions.Item label="Дата завершения">
                      {new Date(selectedAudit.completedAt).toLocaleString()}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Card>
            </Col>

            {/* Результаты по критериям */}
            <Col xs={24} lg={8}>
              <Card title="Результаты по критериям" size="small">
                <List
                  dataSource={selectedAudit.auditResults}
                  renderItem={(result) => (
                    <List.Item>
                      <List.Item.Meta
                        title={
                          <Space>
                            <Text strong style={{ textTransform: 'capitalize' }}>
                              {result.criterion.replace('_', ' ')}
                            </Text>
                            <Progress
                              type="circle"
                              size={30}
                              percent={result.score}
                              status={getScoreColor(result.score)}
                              format={() => `${result.score.toFixed(0)}`}
                            />
                          </Space>
                        }
                        description={result.feedback}
                      />
                    </List.Item>
                  )}
                />
              </Card>
            </Col>

            {/* Отчет и рекомендации */}
            <Col xs={24} lg={8}>
              <Card title="Отчет ИИ" size="small">
                <Space direction="vertical" style={{ width: '100%' }}>
                  {selectedAudit.aiReport && (
                    <div>
                      <Text strong>Анализ:</Text>
                      <div style={{ 
                        maxHeight: 200, 
                        overflowY: 'auto', 
                        padding: 8, 
                        backgroundColor: '#f5f5f5', 
                        borderRadius: 4,
                        marginTop: 8
                      }}>
                        <Text>{selectedAudit.aiReport}</Text>
                      </div>
                    </div>
                  )}
                  
                  {selectedAudit.recommendations && (
                    <div>
                      <Text strong>Рекомендации:</Text>
                      <div style={{ 
                        maxHeight: 200, 
                        overflowY: 'auto', 
                        padding: 8, 
                        backgroundColor: '#f6ffed', 
                        borderRadius: 4,
                        marginTop: 8
                      }}>
                        <Text>{selectedAudit.recommendations}</Text>
                      </div>
                    </div>
                  )}
                </Space>
              </Card>
            </Col>
          </Row>
        )}
      </Modal>
    </div>
  );
};

export default CourseAuditor;
